import { useEffect, useState } from 'react';
import { Link as RouterLink, useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { ZendeskArticle } from 'interfaces/ZendeskArticle';
import {
  Collapse,
  Divider,
  IconButton,
  Link,
  List,
  ListItem,
  ListItemSecondaryAction,
  ListItemText,
  MenuItem,
  MenuList,
  Theme,
  Typography,
  useMediaQuery,
} from '@mui/material';
import { ExpandLess, ExpandMore } from '@mui/icons-material';
import { styled } from '@mui/material/styles';

import { MyToverAPI } from 'api/MyToverAPI';

const PREFIX = 'ArticleSectionMenu';

const classes = {
  root: `${PREFIX}-root`,
};

const Root = styled('aside')(({ theme }) => ({
  [theme.breakpoints.up('md')]: {
    'marginTop': 100,
    'maxWidth': '100%',
    '&::-webkit-scrollbar': {
      width: 4,
    },
    '&::-webkit-scrollbar-track': {
      background: theme.palette.grey[100],
    },
    '&::-webkit-scrollbar-thumb': {
      background: theme.palette.grey[300],
    },
  },
}));

interface SectionMenuProps {
  slug?: string;
  category?: number;
}

const ArticleSectionMenu = ({ slug, category: id }: SectionMenuProps) => {
  const { t, i18n } = useTranslation();
  const location = useLocation();
  const [sectionArticles, setSectionArticles] = useState<ZendeskArticle[]>([]);
  const [categoryRoute, setCategoryRoute] = useState<string>(`/categories/${id}`);

  const getSectionArticles = async () => {
    try {
      const { success, parsed } = await MyToverAPI.getCategory(id!.toString());
      if (success) {
        setSectionArticles(parsed.data.articles);
        setCategoryRoute(parsed.data.route);
      }
    } catch (error) {
      console.log(error);
    }
  };

  useEffect(() => {
    if (id) getSectionArticles();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id, i18n.language, location.pathname]);

  const StyledMenuItem = styled(MenuItem)(({ theme }) => ({
    'backgroundColor': 'transparent',
    'borderLeftColor': theme.palette.grey[100],
    'borderLeftStyle': 'solid',
    'borderLeftWidth': 2,
    'fontSize': '1rem',
    'padding': `${theme.spacing(1)} ${theme.spacing(1.5)}`,
    'whiteSpace': 'normal',
    '&:hover': {
      backgroundColor: 'transparent',
    },
    '&:focus': {
      backgroundColor: theme.palette.primary.main,
    },
    '&.Mui-selected': {
      'backgroundColor': 'transparent',
      'borderLeftColor': theme.palette.primary.main,
      '&:hover': {
        backgroundColor: 'transparent',
      },
      '&:focus': {
        backgroundColor: theme.palette.primary.main,
      },
    },
    '&.MuiListItem-button:hover': {
      backgroundColor: 'transparent',
    },
  }));

  const [expanded, setExpanded] = useState<boolean | undefined>();
  const onMobileAndTablet = useMediaQuery((theme: Theme) => theme.breakpoints.down('md'));

  useEffect(() => {
    if (onMobileAndTablet) {
      setExpanded(false);
    } else {
      setExpanded(true);
    }
  }, [onMobileAndTablet]);

  return (
    <Root className={classes.root}>
      <List sx={{ '&:hover': { backgroundColor: 'grey.100', cursor: 'pointer' } }}>
        <ListItem onClick={() => setExpanded(!expanded)}>
          <ListItemText primary={t('article.section')} primaryTypographyProps={{ variant: 'body1' }} />
          <ListItemSecondaryAction>
            <IconButton disableRipple edge="end" aria-label="expand" onClick={() => setExpanded(!expanded)}>
              {expanded ? <ExpandLess /> : <ExpandMore />}
            </IconButton>
          </ListItemSecondaryAction>
        </ListItem>
      </List>

      <Divider variant="middle" />

      <Collapse in={expanded}>
        <MenuList>
          {sectionArticles.slice(0, 10).map((article) => (
            /**
             * TODO: property component on MenuItem will be fixed in v5?
             * https://github.com/mui-org/material-ui/issues/21210
             */
            <Link key={article.id} component={RouterLink} to={article.route} underline="hover">
              <StyledMenuItem dense disableGutters selected={article.slug === slug}>
                <Typography variant="inherit" component="span">
                  {article.title}
                </Typography>
              </StyledMenuItem>
            </Link>
          ))}
          {sectionArticles.length >= 10 && (
            <Link underline="always" component={RouterLink} to={categoryRoute} p={2}>
              {t('article.parent')}
            </Link>
          )}
        </MenuList>
      </Collapse>
    </Root>
  );
};

export default ArticleSectionMenu;
